<template>
  <transition name="modal-fade">
    <div v-if="isOpen" class="youtube-player-modal" @click.self="closePlayer">
      <div class="modal-container">
        <!-- Header del modal -->
        <div class="modal-header">
          <h3 class="modal-title">{{ videoTitle }}</h3>
          <button v-if="canFloat" @click="openFloating" class="close-btn" title="Ventana flotante">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="2" y="4" width="20" height="16" rx="2"/>
              <rect x="12" y="12" width="8" height="6" rx="1" fill="currentColor"/>
            </svg>
          </button>
          <button @click="closePlayer" class="close-btn" title="Cerrar (Esc)">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <!-- Reproductor de YouTube -->
        <div class="player-wrapper">
          <iframe
            v-if="videoId"
            ref="iframe"
            :src="embedUrl"
            @load="listenYouTube"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
            playsinline
            class="youtube-iframe"
          ></iframe>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'YouTubePlayer',
  data() {
    return {
      isOpen: false,
      videoId: null,
      videoTitle: '',
      currentTime: 0,
      startTime: 0,
      canFloat: 'documentPictureInPicture' in window
    };
  },
  computed: {
    embedUrl() {
      if (!this.videoId) return '';
      // Parámetros del embed:
      // autoplay=1 - Reproduce automáticamente
      // rel=0 - No mostrar videos relacionados de otros canales
      // modestbranding=1 - Ocultar logo de YouTube en la barra de control
      // fs=1 - Habilitar botón de pantalla completa (fullscreen)
      // playsinline=1 - Reproducir inline en iOS (necesario para fullscreen con audio)
      // enablejsapi=1 - Recibir el tiempo actual para continuar en la ventana flotante
      return `https://www.youtube.com/embed/${this.videoId}?autoplay=1&rel=0&modestbranding=1&fs=1&playsinline=1&enablejsapi=1&start=${Math.floor(this.startTime)}`;
    }
  },
  methods: {
    openPlayer(videoId, title, startTime = 0) {
      this.startTime = startTime;
      this.videoId = videoId;
      this.videoTitle = title;
      this.isOpen = true;
      document.body.style.overflow = 'hidden'; // Prevenir scroll del body
    },
    listenYouTube() {
      // Handshake para que YouTube envíe infoDelivery (incluye currentTime)
      this.$refs.iframe.contentWindow.postMessage(JSON.stringify({ event: 'listening', id: 1, channel: 'widget' }), 'https://www.youtube.com');
    },
    handlePlayerMessage(event) {
      if (event.origin !== 'https://www.youtube.com' || !this.$refs.iframe || event.source !== this.$refs.iframe.contentWindow) return;
      let data = event.data;
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch (e) { return; }
      }
      if (data && data.info && typeof data.info.currentTime === 'number') this.currentTime = data.info.currentTime;
    },
    // Document Picture-in-Picture: ventana siempre encima (Chrome/Edge escritorio)
    async openFloating() {
      const pip = await window.documentPictureInPicture.requestWindow({ width: 480, height: 270 });
      pip.document.title = this.videoTitle;
      pip.document.body.style.margin = '0';
      const iframe = document.createElement('iframe');
      iframe.src = `/pip.html?v=${encodeURIComponent(this.videoId)}&t=${Math.floor(this.currentTime)}`;
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      iframe.style.cssText = 'border:0;width:100vw;height:100vh;display:block';
      pip.document.body.append(iframe);
      // Al cerrar la ventana (X o "volver a la pestaña") el video vuelve al modal donde iba
      const { videoId, videoTitle } = this;
      pip.addEventListener('pagehide', () => {
        if (this._isDestroyed) return;
        const t = iframe.contentWindow && iframe.contentWindow.currentTime;
        this.openPlayer(videoId, videoTitle, typeof t === 'number' ? t : 0);
      });
      this.closePlayer();
    },
    closePlayer() {
      this.isOpen = false;
      this.videoId = null;
      this.videoTitle = '';
      this.currentTime = 0;
      document.body.style.overflow = ''; // Restaurar scroll
    },
    handleEscKey(event) {
      if (event.key === 'Escape' && this.isOpen) {
        this.closePlayer();
      }
    }
  },
  mounted() {
    // Escuchar tecla ESC para cerrar
    window.addEventListener('keydown', this.handleEscKey);
    window.addEventListener('message', this.handlePlayerMessage);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleEscKey);
    window.removeEventListener('message', this.handlePlayerMessage);
    document.body.style.overflow = '';
  }
};
</script>

<style scoped>
/* Modal overlay */
.youtube-player-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.95);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow: hidden;
}

.modal-container {
  /* Limitar por alto para que el header (botones flotante/cerrar) no quede fuera en pantallas bajas: 120px = padding + header + gap */
  width: min(100%, calc((100vh - 120px) * 16 / 9));
  max-width: 1100px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
}

.modal-title {
  color: white;
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  flex: 1;
  line-height: 1.3;
}

.close-btn {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.05);
}

/* Player wrapper */
.player-wrapper {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  background: #000;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.8);
}

.youtube-iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}


/* Animations */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .modal-container,
.modal-fade-leave-active .modal-container {
  transition: transform 0.3s ease;
}

.modal-fade-enter-from .modal-container,
.modal-fade-leave-to .modal-container {
  transform: scale(0.9);
}

/* Responsive */
@media (min-width: 1400px) {
  .modal-container {
    max-width: 1400px;
  }
}

@media (max-width: 1024px) {
  .youtube-player-modal {
    padding: 30px;
  }

  .modal-container {
    max-width: 900px;
  }
}

@media (max-width: 768px) {
  .youtube-player-modal {
    padding: 15px;
  }

  .modal-container {
    max-height: calc(100vh - 30px);
    max-width: 95%;
    gap: 8px;
  }

  .modal-header {
    gap: 10px;
  }

  .modal-title {
    font-size: 15px;
  }

  .close-btn {
    width: 36px;
    height: 36px;
  }

  .player-wrapper {
    border-radius: 8px;
  }
}

@media (max-width: 480px) {
  .youtube-player-modal {
    padding: 12px;
  }

  .modal-container {
    max-width: 98%;
  }

  .modal-title {
    font-size: 14px;
  }
}
</style>
